import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Heart, Trash2, ExternalLink } from "lucide-react";
import { FavoriteLeadingMedia } from "@/components/favorites/FavoriteLeadingMedia";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Seo } from "@/components/common/Seo";
import { useFavorites } from "@/components/favorites/FavoritesProvider";
import type { FavoriteKind, FavoriteItem } from "@/lib/favorites/types";
import { resolveFavoritesAgainstPublicTargets } from "@/lib/favorites/favoritesSync";
import { logSystemEvent } from "@/lib/logging/logSystemEvent";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { track, setSpecSurface } from "@/lib/analytics";

const TABS: { label: string; kind: FavoriteKind | null }[] = [
  { label: "Alla", kind: null },
  { label: "Platser", kind: "place" },
  { label: "Aktiviteter", kind: "activity" },
  { label: "Värdar", kind: "producer" },
];

function kindLabel(kind: FavoriteKind): string {
  switch (kind) {
    case "place": return "Plats";
    case "activity": return "Aktivitet";
    case "producer": return "Värd";
  }
}

function favoriteHref(f: FavoriteItem): string | null {
  const slug = f.slug || f.entityId;
  switch (f.kind) {
    case "place": return `/place/${slug}`;
    case "activity": return `/event/${slug}`;
    case "producer": return `/producer/${slug}`;
    default: return null;
  }
}

export default function FavoritesPage() {
  const [activeKind, setActiveKind] = useState<FavoriteKind | null>(null);
  const { listFavorites, toggleFavorite } = useFavorites();
  const allItems = listFavorites();
  const [resolvedAllItems, setResolvedAllItems] = useState<FavoriteItem[]>(allItems);
  const didTrackOpen = useRef(false);

  const itemsSignature = useMemo(
    () => allItems.map((item) => `${item.kind}:${item.entityId}:${item.savedAt}`).join("|"),
    [allItems]
  );

  const items = useMemo(() => {
    if (!activeKind) return resolvedAllItems;
    return resolvedAllItems.filter((item) => item.kind === activeKind);
  }, [activeKind, resolvedAllItems]);

  useEffect(() => {
    setSpecSurface("explore_list");
    if (!didTrackOpen.current) {
      track("favorites_opened", { filter_kind: "all" }, "router");
      didTrackOpen.current = true;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolveItems() {
      try {
        const { validItems, prunedItems } = await resolveFavoritesAgainstPublicTargets(allItems);
        if (cancelled) return;

        setResolvedAllItems(validItems);

        if (prunedItems.length > 0) {
          await logSystemEvent({
            level: "info",
            source: "ui",
            eventKey: "favorites.saved.pruned_on_load",
            message: "Pruned unresolvable favorites on saved page load",
            domain: "favorites",
            context: {
              pruned_count: prunedItems.length,
              pruned_keys: prunedItems.map((item) => `${item.kind}:${item.entityId}`),
            },
          });
        }
      } catch (error) {
        if (cancelled) return;
        setResolvedAllItems(allItems);
        await logSystemEvent({
          level: "warn",
          source: "ui",
          eventKey: "favorites.saved.resolve_failed",
          message: "Failed to resolve favorites against public targets",
          domain: "favorites",
          context: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
    }

    resolveItems();

    return () => {
      cancelled = true;
    };
  }, [itemsSignature]);

  const handleTabChange = useCallback((kind: FavoriteKind | null) => {
    setActiveKind(kind);
    track("favorites_opened", { filter_kind: kind ?? "all" }, "router");
  }, []);

  const handleRemove = useCallback(
    (item: FavoriteItem) => {
      toggleFavorite({
        kind: item.kind,
        entityId: item.entityId,
        slug: item.slug,
        title: item.title,
        imagePath: item.imagePath,
      });
      toast("Borttagen", {
        action: {
          label: "Ångra",
          onClick: () => {
            toggleFavorite({
              kind: item.kind,
              entityId: item.entityId,
              slug: item.slug,
              title: item.title,
              imagePath: item.imagePath,
            });
          },
        },
      });
    },
    [toggleFavorite]
  );

  return (
    <div className="flex min-h-screen flex-col">
      <Seo title="Sparat – Farmly" description="Dina sparade platser, aktiviteter och värdar." />
      <SiteHeader />
      <main className="flex-1 container max-w-2xl px-4 py-12 md:py-20">
        {/* Header */}
        <div className="space-y-2 mb-8">
          <h1 className="font-display text-2xl md:text-3xl font-medium text-foreground">
            Sparat
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Här hamnar det du vill återvända till.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 mb-8 overflow-x-auto pb-1">
          {TABS.map((tab) => (
            <button
              key={tab.label}
              onClick={() => handleTabChange(tab.kind)}
              className={cn(
                "px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap",
                activeKind === tab.kind
                  ? "bg-foreground text-background"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* List */}
        {items.length === 0 ? (
          <EmptyState activeKind={activeKind} />
        ) : (
          <ul className="space-y-3">
            {items.map((item) => (
              <FavoriteCard
                key={`${item.kind}:${item.entityId}`}
                item={item}
                onRemove={handleRemove}
              />
            ))}
          </ul>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function EmptyState({ activeKind }: { activeKind: FavoriteKind | null }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
      <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
        <Heart className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          {activeKind ? `Inga sparade ${kindLabel(activeKind).toLowerCase()}ar` : "Inget sparat ännu"}
        </p>
        <p className="text-xs text-muted-foreground">
          Tryck på hjärtat för att spara det du vill återvända till.
        </p>
      </div>
      <Button variant="outline" size="sm" asChild>
        <Link to="/explore">Utforska</Link>
      </Button>
    </div>
  );
}

function FavoriteCard({
  item,
  onRemove,
}: {
  item: FavoriteItem;
  onRemove: (item: FavoriteItem) => void;
}) {
  const href = favoriteHref(item);

  return (
    <li className="group flex items-center gap-3 rounded-xl border border-border/40 bg-card p-3 transition-colors hover:bg-muted/30">
      {/* Thumbnail */}
      <FavoriteLeadingMedia
        kind={item.kind}
        imageUrl={item.imagePath}
        alt={item.title ?? ""}
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {item.title || "Okänd"}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {kindLabel(item.kind)}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {href ? (
          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
            <Link to={href} aria-label="Öppna">
              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
            </Link>
          </Button>
        ) : (
          <span className="text-[10px] text-muted-foreground/60 px-1">
            Kan inte öppnas
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(item)}
          aria-label="Ta bort"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  );
}
